import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useFolder } from "src/core/contexts/FolderContext";
import {
  UpsertDiligenceFormValues,
  upsertDiligenceSchema,
} from "src/core/forms/folder/upsertDiligence.schema";
import {
  Diligence,
  useCreateDiligenceMutation,
  useUpdateDiligenceMutation,
} from "src/core/gql/graphql";
import { toast } from "src/core/hooks/use-toast";

export const useUpsertDiligenceForm = (
  setOpen: (open: boolean) => void,
  diligence: Diligence | null,
  setDiligenceToEdit: (diligence: Diligence | null) => void
) => {
  const [updateDiligence] = useUpdateDiligenceMutation();
  const [createDiligence] = useCreateDiligenceMutation();
  const [isLoading, setIsLoading] = useState(false);
  const { folderFile, refetch } = useFolder();
  const form = useForm<UpsertDiligenceFormValues>({
    resolver: zodResolver(upsertDiligenceSchema()),
    defaultValues: {
      name: diligence?.name || "",
    },
  });

  useEffect(() => {
    if (diligence) {
      form.reset({
        name: diligence.name,
      });
    } else {
      form.reset({
        name: "",
      });
    }
  }, [diligence, form]);

  const onSubmit = useCallback(
    async (data: UpsertDiligenceFormValues) => {
      if (diligence?.id) {
        try {
          setIsLoading(true);
          await updateDiligence({ variables: { diligenceId: diligence.id, name: data.name } });
          await refetch();
          setOpen(false);
          setDiligenceToEdit(null);
          toast({
            description: t("form.editDiligence.success"),
          });
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      } else if (folderFile?.folderFile?.id) {
        try {
          setIsLoading(true);
          await createDiligence({
            variables: { folderFileId: folderFile?.folderFile?.id, name: data.name },
          });
          await refetch();
          setOpen(false);
          toast({
            description: t("form.addDiligence.success"),
          });
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [
      setOpen,
      setDiligenceToEdit,
      refetch,
      updateDiligence,
      createDiligence,
      diligence,
      folderFile?.folderFile?.id,
    ]
  );

  return {
    form,
    onSubmit,
    isLoading,
  };
};
